import { BrandLoader } from "@/components/brand";
import {
  Check,
  FolderInput,
  ListChecks,
  MoreHorizontal,
  Plus
} from "lucide-react";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type FC,
  type KeyboardEvent
} from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { Field, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import { Textarea } from "@/components/ui/textarea";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

import { CaseAgentAPI } from "../chat/api/caseAgentApi";
import { useCaseIde, useOnInvalidate } from "../context/CaseIdeContext";
import { PLAN_STATUS_LABEL, todoProgress } from "../plan/model";
import { PlanProse } from "../plan/PlanProse";
import { PlanTodos } from "../plan/PlanTodos";
import { PLAN_ACCENT_TEXT, PLAN_BUILD_BUTTON } from "../plan/theme";
import type { CaseAgentPlan, PlanTodo } from "../types";

type Props = { planId: string };

type Draft = {
  title: string;
  overview: string;
  markdown: string;
  todos: PlanTodo[];
};

const draftOf = (plan: CaseAgentPlan): Draft => ({
  title: plan.title,
  overview: plan.overview,
  markdown: plan.markdown,
  todos: plan.todos.map((t) => ({ ...t }))
});

let newTodoSeq = 0;

/**
 * A plan open in the center pane, laid out the way Cursor's plan editor lays
 * out a plan: a slim toolbar (status, Edit, Build) over a centered document
 * with the title, the overview, the to-dos and the markdown body.
 *
 * Read mode refetches on `plans` invalidations so todo checkmarks move while
 * the agent builds; edit mode holds the attorney's changes until Save (an
 * agent update meanwhile only toasts). Build hands the plan to the chat.
 */
export const PlanPane: FC<Props> = ({ planId }) => {
  const { individualId, requestPlanBuild, invalidate } = useCaseIde();
  const [plan, setPlan] = useState<CaseAgentPlan | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [saving, setSaving] = useState(false);
  const [newTodo, setNewTodo] = useState("");
  const editingRef = useRef(false);
  editingRef.current = draft !== null;

  const load = useCallback(async () => {
    try {
      const next = await CaseAgentAPI.getPlan(individualId, planId);
      setPlan(next);
      setError(null);
      return next;
    } catch {
      setError("Couldn't load this plan.");
      return null;
    }
  }, [individualId, planId]);

  useEffect(() => {
    setPlan(null);
    setDraft(null);
    void load();
  }, [load]);

  useOnInvalidate(["plans"], () => {
    if (editingRef.current) {
      toast.message("Plan updated by the agent", {
        description:
          "Your edits are kept; save or cancel to see the new version."
      });
      return;
    }
    void load();
  });

  const startEdit = () => plan && setDraft(draftOf(plan));
  const cancelEdit = () => {
    setDraft(null);
    setNewTodo("");
  };

  const save = async () => {
    if (!plan || !draft) return;
    setSaving(true);
    try {
      const next = await CaseAgentAPI.updatePlan(individualId, plan.id, {
        title: draft.title.trim() || plan.title,
        overview: draft.overview,
        markdown: draft.markdown,
        todos: draft.todos.map((t) =>
          t.id.startsWith("new-")
            ? { content: t.content }
            : { id: t.id, content: t.content }
        )
      });
      setPlan(next);
      setDraft(null);
      setNewTodo("");
      invalidate(["plans"]);
    } catch {
      toast.error("Couldn't save the plan.");
    } finally {
      setSaving(false);
    }
  };

  // "Save to Work Products": file the plan under the Work Products Plans folder.
  // Same document, so the pane keeps following it; the tree picks it up on
  // the `plans` invalidation.
  const [filing, setFiling] = useState(false);
  const saveToWorkspace = async () => {
    if (!plan || plan.saved_to_workspace) return;
    setFiling(true);
    try {
      const next = await CaseAgentAPI.savePlanToWorkspace(
        individualId,
        plan.id
      );
      setPlan(next);
      invalidate(["plans"]);
      toast.success("Saved to Work Products", {
        description: `“${next.title}” is filed under Plans.`
      });
    } catch {
      toast.error("Couldn't save the plan to the workspace.");
    } finally {
      setFiling(false);
    }
  };

  const patch = (changes: Partial<Draft>) =>
    setDraft((d) => (d ? { ...d, ...changes } : d));
  const removeTodo = (id: string) =>
    setDraft((d) =>
      d ? { ...d, todos: d.todos.filter((t) => t.id !== id) } : d
    );
  const moveTodo = (id: string, direction: -1 | 1) =>
    setDraft((d) => {
      if (!d) return d;
      const index = d.todos.findIndex((t) => t.id === id);
      const target = index + direction;
      if (index < 0 || target < 0 || target >= d.todos.length) return d;
      const todos = [...d.todos];
      [todos[index], todos[target]] = [todos[target], todos[index]];
      return { ...d, todos };
    });
  const addTodo = () => {
    const content = newTodo.trim();
    if (!content) return;
    newTodoSeq += 1;
    setDraft((d) =>
      d
        ? {
            ...d,
            todos: [
              ...d.todos,
              { id: `new-${newTodoSeq}`, content, status: "pending" }
            ]
          }
        : d
    );
    setNewTodo("");
  };
  const onNewTodoKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addTodo();
    }
  };

  if (error) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
        {error}
      </div>
    );
  }
  if (!plan) {
    return (
      <div className="flex h-full items-center justify-center">
        <BrandLoader label="Loading plan" />
      </div>
    );
  }

  const editing = draft !== null;
  const progress = todoProgress(plan.todos);
  const canBuild = plan.status === "draft" && !editing;
  const buildTooltip =
    plan.status === "building"
      ? "Already building"
      : plan.status === "complete"
      ? "Already complete"
      : editing
      ? "Save or cancel your edits first"
      : "Switch to Agent mode and carry out this plan";

  return (
    <div
      className="flex h-full min-h-0 flex-col bg-background text-foreground"
      data-testid="plan-pane"
    >
      <div className="flex h-9 shrink-0 items-center gap-2 border-b border-border bg-background px-3">
        <ListChecks
          className={cn("size-3.5 shrink-0", PLAN_ACCENT_TEXT)}
          aria-hidden
        />
        <span className="text-sm font-medium text-fg-2">Plan</span>
        <span
          className="flex min-w-0 items-center gap-1.5 truncate text-sm text-muted-foreground"
          data-testid="plan-status"
        >
          {plan.status === "building" ? (
            <Spinner className={cn("size-3", PLAN_ACCENT_TEXT)} />
          ) : plan.status === "complete" ? (
            <Check className="size-3" strokeWidth={2.5} />
          ) : null}
          {PLAN_STATUS_LABEL[plan.status]} · {progress.label}
        </span>
        <div className="ml-auto flex items-center gap-1">
          {editing ? (
            <>
              <Button
                type="button"
                variant="ghost"
                size="xs"
                className="text-muted-foreground"
                onClick={cancelEdit}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                type="button"
                size="xs"
                onClick={() => void save()}
                disabled={saving}
              >
                Save
              </Button>
            </>
          ) : (
            <Button
              type="button"
              variant="ghost"
              size="xs"
              className="text-muted-foreground hover:text-foreground"
              onClick={startEdit}
              disabled={plan.status === "complete"}
            >
              Edit
            </Button>
          )}
          <Tooltip>
            <TooltipTrigger asChild>
              <span tabIndex={canBuild ? -1 : 0} className="inline-flex">
                <Button
                  variant="primary"
                  type="button"
                  size="xs"
                  className={cn("px-2.5 font-medium", PLAN_BUILD_BUTTON)}
                  disabled={!canBuild}
                  onClick={() => requestPlanBuild(plan.id)}
                >
                  Build
                </Button>
              </span>
            </TooltipTrigger>
            <TooltipContent side="bottom">{buildTooltip}</TooltipContent>
          </Tooltip>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon-xs"
                className="size-6 text-muted-foreground hover:text-foreground"
                aria-label="More plan actions"
                data-testid="plan-more"
              >
                <MoreHorizontal className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-52">
              {plan.saved_to_workspace ? (
                <DropdownMenuItem
                  disabled
                  data-testid="plan-saved-to-workspace"
                >
                  <Check className="size-3.5" />
                  Saved to Work Products
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem
                  disabled={editing || filing}
                  onSelect={() => void saveToWorkspace()}
                  data-testid="plan-save-to-workspace"
                >
                  <FolderInput className="size-3.5" />
                  Save to Work Products
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-auto">
        <div className="mx-auto w-full max-w-3xl px-8 pt-8 pb-16">
          {editing ? (
            <div className="space-y-6">
              <Input
                value={draft.title}
                onChange={(e) => patch({ title: e.target.value })}
                aria-label="Plan title"
                className="h-10 border-transparent bg-transparent px-0 text-xl font-semibold tracking-tight shadow-none focus-visible:border-border md:text-xl"
              />
              <Field>
                <FieldLabel
                  htmlFor="plan-overview"
                  className="text-xs font-medium tracking-wide text-muted-foreground uppercase"
                >
                  Overview
                </FieldLabel>
                <Textarea
                  id="plan-overview"
                  value={draft.overview}
                  onChange={(e) => patch({ overview: e.target.value })}
                  className="min-h-16 text-md leading-relaxed"
                />
              </Field>
              <div className="space-y-2">
                <PlanTodos
                  todos={draft.todos}
                  onRemove={removeTodo}
                  onMove={moveTodo}
                />
                <div className="flex items-center gap-2">
                  <Input
                    value={newTodo}
                    onChange={(e) => setNewTodo(e.target.value)}
                    onKeyDown={onNewTodoKey}
                    placeholder="Add a to-do…"
                    aria-label="New todo"
                    className="h-8 text-base"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-8 gap-1 text-xs"
                    onClick={addTodo}
                    disabled={!newTodo.trim()}
                  >
                    <Plus className="size-3" /> Add
                  </Button>
                </div>
              </div>
              <Field>
                <FieldLabel
                  htmlFor="plan-markdown"
                  className="text-xs font-medium tracking-wide text-muted-foreground uppercase"
                >
                  Plan
                </FieldLabel>
                <Textarea
                  id="plan-markdown"
                  value={draft.markdown}
                  onChange={(e) => patch({ markdown: e.target.value })}
                  className="min-h-96 font-mono text-sm leading-relaxed"
                />
              </Field>
            </div>
          ) : (
            <article className="space-y-6">
              <header className="space-y-2">
                <h1 className="text-xl leading-tight font-semibold tracking-tight text-foreground">
                  {plan.title}
                </h1>
                {plan.overview ? (
                  <p className="text-md leading-[1.65] text-muted-foreground">
                    {plan.overview}
                  </p>
                ) : null}
              </header>
              {plan.todos.length ? <PlanTodos todos={plan.todos} /> : null}
              {plan.markdown ? (
                <PlanProse>{plan.markdown}</PlanProse>
              ) : (
                <p className="text-md text-muted-foreground italic">
                  The plan has no details yet.
                </p>
              )}
            </article>
          )}
        </div>
      </div>
    </div>
  );
};
